import { ChangeEventHandler, FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";

import { faFileDownload, faTrash } from "@fortawesome/pro-solid-svg-icons";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { useTermsOfUseService } from "../../../../../services/ServiceHooks/termsOfUseHook";
import { useStore } from "../../../../../stores/StoreContext";
import { ColumnActionIcon } from "../../../../Shared/ColumnActionIcon/ColumnActionIcon";
import { confirmAction } from "../../../../Shared/Functions/ConfirmActionDialogFunction";
import { TableSkeleton } from "../../../../Shared/LoadingSkeletons/TableSkeleton/TableSkeleton";

import "./TermsOfUseTable.scss";

export interface ITermsOfUse {
  id: string;
  originalFileName: string;
  filePath: string;
  version: number;
}

interface ITermsOfUseTableProps {
  terms: ITermsOfUse[];
  isLoading: boolean;
  onReloadTable: () => void;
  onDeleteEntry: (entry: ITermsOfUse) => void;
}

const TermsOfUseTable: FunctionComponent<ITermsOfUseTableProps> = (props: ITermsOfUseTableProps) => {
  const { t } = useTranslation();
  const { userStore } = useStore();

  const termsService = useTermsOfUseService();
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const touTranslationKeys = translationKeys.adminArea.termsOfUse;

  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>({
    global: { value: null, matchMode: "contains" },
  });

  const downloadTermsFile = async (rowData: ITermsOfUse): Promise<void> => {
    const base64 = await termsService.getTermsOfUseById(rowData.id);
    if (!base64) {
      return;
    }

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = rowData.originalFileName ?? "terms-of-use.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFilterChange: ChangeEventHandler<HTMLInputElement> = e => {
    setGlobalFilterValue(e.target.value);
    const extendedFilters = { ...filters };
    (extendedFilters["global"] as any).value = String(e.target.value).trim();
    setFilters(extendedFilters);
  };

  const resetFilter = (): void => {
    setGlobalFilterValue("");
    const reducedFilters = { ...filters };
    (reducedFilters["global"] as any).value = null;
    setFilters(reducedFilters);
  };

  const TableHeader = useMemo(() => {
    return (
      <div className="flex justify-content-end">
        <div className="p-icon-field p-icon-field-left p-icon-field-right">
          <i className="pi pi-search p-input-icon" style={{ width: "fit-content" }} />

          <InputText
            value={globalFilterValue}
            onChange={onFilterChange}
            placeholder="Filter terms of use"
            className="pr-5"
          />

          {globalFilterValue?.length > 0 && (
            <i
              className="pi pi-times cursor-pointer p-link p-input-icon"
              onClick={resetFilter}
              onKeyDown={event => event.key === "Enter" && resetFilter()}
              role="button"
              tabIndex={0}
            />
          )}
        </div>
      </div>
    );
  }, [globalFilterValue, userStore.currentLanguage]);

  const TermsTableMemo = useMemo(() => {
    if (props.isLoading) {
      return <TableSkeleton rows={2} columns={4} />;
    }

    return (
      <div className="terms-table">
        <DataTable
          key={`${filters ? Object.keys(filters).length : 0}-${userStore.currentLanguage}`}
          value={props.terms}
          header={TableHeader}
          paginator
          scrollable
          scrollHeight="25rem"
          sortField="version"
          sortOrder={-1}
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          filters={filters}
          globalFilterFields={["originalFileName", "version"]}
          onFilter={(e: DataTableStateEvent) => setFilters(e.filters)}
          size="small"
          emptyMessage={t(touTranslationKeys.notFound)}
        >
          <Column
            field="originalFileName"
            sortable
            header={t(touTranslationKeys.fields.fileName)}
            style={{ minWidth: "220px" }}
          />

          <Column field="version" sortable header={t(touTranslationKeys.fields.version)} style={{ width: "90px" }} />

          <Column field="filePath" header={t(touTranslationKeys.fields.filePath)} style={{ minWidth: "260px" }} />

          <Column
            style={{ width: "110px" }}
            headerStyle={{ width: "110px" }}
            frozen
            alignFrozen="right"
            body={(rowData: ITermsOfUse) => (
              <div className="terms-of-use-actions">
                <ColumnActionIcon
                  rowData={rowData}
                  icon={faFileDownload}
                  tooltipTranslationKey={touTranslationKeys.actions.download}
                  executeAction={() => downloadTermsFile(rowData)}
                />

                <ColumnActionIcon
                  rowData={rowData}
                  icon={faTrash}
                  tooltipTranslationKey={touTranslationKeys.actions.delete.name}
                  className="danger"
                  confirmAction={() =>
                    confirmAction(t, {
                      acceptLabel: t(touTranslationKeys.actions.delete.name),
                      message: t(touTranslationKeys.actions.delete.confirmationMessage, {
                        name: rowData.originalFileName,
                      }),
                    })
                  }
                  executeAction={() => props.onDeleteEntry(rowData)}
                />
              </div>
            )}
          />
        </DataTable>
      </div>
    );
  }, [props.terms, props.isLoading, filters, userStore.currentLanguage]);

  return TermsTableMemo;
};

export default TermsOfUseTable;
